import { useState, useEffect, useMemo } from 'react';
import { useOnboardingStore } from '../../../store/onboardingStore';
import { useAuth } from '../../../context/AuthContext';
import { dictionary, Language } from '../../../utils/dictionary';
import { FunnelConfig, SalesAngle, LegacyTemplate, LEGACY_TEMPLATES } from '../../../types/onboarding';
import { Check, ArrowLeft, ArrowRight, Pencil, ChevronDown, ChevronUp, Sparkles, Target, BarChart3 } from 'lucide-react';
import { Clock, Users, Shield, Zap, Leaf } from 'lucide-react';
import Loader from '../../common/Loader';
import { SectionTemplate } from '../../../services/funnels';
import type { AdStyle } from '../../../feature/ads/cretives/components/modals/steps/adStudio/types';
import { LazyImage } from '../../ui/lazy-image';

// Word-level translation map for template names
const TEMPLATE_WORD_TRANSLATIONS: Record<string, Record<string, string>> = {
  'tecnologia': { en: 'Technology', pt: 'Tecnologia', es: 'Tecnología' },
  'neon': { en: 'Neon', pt: 'Neon', es: 'Neón' },
  'verde': { en: 'Green', pt: 'Verde', es: 'Verde' },
  'azul': { en: 'Blue', pt: 'Azul', es: 'Azul' },
  'rojo': { en: 'Red', pt: 'Vermelho', es: 'Rojo' },
  'negro': { en: 'Black', pt: 'Preto', es: 'Negro' },
  'blanco': { en: 'White', pt: 'Branco', es: 'Blanco' },
  'dorado': { en: 'Gold', pt: 'Dourado', es: 'Dorado' },
  'elegante': { en: 'Elegant', pt: 'Elegante', es: 'Elegante' },
  'moderno': { en: 'Modern', pt: 'Moderno', es: 'Moderno' },
  'clasico': { en: 'Classic', pt: 'Clássico', es: 'Clásico' },
  'minimalista': { en: 'Minimalist', pt: 'Minimalista', es: 'Minimalista' },
  'profesional': { en: 'Professional', pt: 'Profissional', es: 'Profesional' },
  'premium': { en: 'Premium', pt: 'Premium', es: 'Premium' },
  'natural': { en: 'Natural', pt: 'Natural', es: 'Natural' },
  'salud': { en: 'Health', pt: 'Saúde', es: 'Salud' },
  'belleza': { en: 'Beauty', pt: 'Beleza', es: 'Belleza' },
  'fitness': { en: 'Fitness', pt: 'Fitness', es: 'Fitness' },
  'deportivo': { en: 'Sport', pt: 'Esportivo', es: 'Deportivo' },
  'hogar': { en: 'Home', pt: 'Casa', es: 'Hogar' },
  'cocina': { en: 'Kitchen', pt: 'Cozinha', es: 'Cocina' },
  'moda': { en: 'Fashion', pt: 'Moda', es: 'Moda' },
  'lujo': { en: 'Luxury', pt: 'Luxo', es: 'Lujo' },
  'agresivo': { en: 'Aggressive', pt: 'Agressivo', es: 'Agresivo' },
  'suave': { en: 'Soft', pt: 'Suave', es: 'Suave' },
  'oscuro': { en: 'Dark', pt: 'Escuro', es: 'Oscuro' },
  'claro': { en: 'Light', pt: 'Claro', es: 'Claro' },
  'vibrante': { en: 'Vibrant', pt: 'Vibrante', es: 'Vibrante' },
  'secadora': { en: 'Dryer', pt: 'Secadora', es: 'Secadora' },
  'maquillaje': { en: 'Makeup', pt: 'Maquiagem', es: 'Maquillaje' },
  'rosa': { en: 'Pink', pt: 'Rosa', es: 'Rosa' },
};

const formatTemplateName = (name: string, lang: string): string => {
  let cleaned = name.replace(/^preview-/i, '');
  const words = cleaned.split('-');
  const langKey = lang === 'en' ? 'en' : lang === 'pt' ? 'pt' : 'es';
  const formatted = words.map(word => {
    const translation = TEMPLATE_WORD_TRANSLATIONS[word.toLowerCase()];
    if (translation && translation[langKey]) {
      return translation[langKey];
    }
    return word.charAt(0).toUpperCase() + word.slice(1);
  });
  return formatted.join(' ');
};

// ─── Main Component ─────────────────────────────────────────
export function StepFunnelConfig({
  onComplete,
  onBack,
  onSubStepChange,
}: {
  onComplete?: () => void;
  onBack?: () => void;
  onSubStepChange?: (subStep: number) => void;
} = {}) {
  const { language } = useAuth();
  const {
    multiFunnelData, updateFunnelConfig, salesAngles,
    sectionTemplates, isFetchingTemplates, fetchSectionTemplates,
    setFunnelDesign, designRecommendation,
    isFetchingSalesAngleRecommendation,
    adTemplates, isFetchingAdTemplates, fetchAdTemplates, toggleFunnelAdTemplate,
    initializeFunnels,
    selectedLegacyTemplates, toggleLegacyTemplate,
  } = useOnboardingStore();

  const [activeSubStep, setActiveSubStepRaw] = useState<1 | 2 | 3>(1);
  const setActiveSubStep = (step: 1 | 2 | 3) => {
    setActiveSubStepRaw(step);
    onSubStepChange?.(step);
  };
  // New (AI) template IDs in selection order
  const [selectedTemplateOrder, setSelectedTemplateOrder] = useState<string[]>([]);
  // For step 3: track which funnel the user is on in the ads sidebar
  const [adsSidebarFunnel, setAdsSidebarFunnel] = useState(0);

  const funnels = multiFunnelData.funnels;
  const newCount = selectedTemplateOrder.length;
  const legacyCount = selectedLegacyTemplates.length;
  const totalSelected = newCount + legacyCount;

  useEffect(() => {
    fetchSectionTemplates();
    fetchAdTemplates();
  }, []);

  // Auto-assign angles when entering sub-step 2
  useEffect(() => {
    if (activeSubStep !== 2 || salesAngles.length === 0) return;
    funnels.forEach((funnel, index) => {
      if (funnel.selectedAngles.length === 0) {
        const angle = salesAngles[index % salesAngles.length];
        updateFunnelConfig(index, {
          selectedAngles: [{ name: angle.id, description: angle.description }],
        });
      }
    });
  }, [activeSubStep, salesAngles.length]);

  // Template selection handler — no limit, free mixing with legacy
  const handleToggleTemplate = (templateId: string, _templateType: string) => {
    setSelectedTemplateOrder(prev => {
      const alreadySelected = prev.includes(templateId);
      if (alreadySelected) {
        return prev.filter(id => id !== templateId);
      }
      return [...prev, templateId];
    });
  };

  // Legacy template selection handler — toggle in/out, free mixing with new
  const handleToggleLegacyTemplate = (template: LegacyTemplate) => {
    toggleLegacyTemplate(template);
  };

  // Angle update handler
  const handleUpdateAngleDescription = (funnelIndex: number, newDescription: string) => {
    const currentAngles = funnels[funnelIndex].selectedAngles;
    if (currentAngles.length > 0) {
      const updated = [{ ...currentAngles[0], description: newDescription }];
      updateFunnelConfig(funnelIndex, { selectedAngles: updated });
      const store = useOnboardingStore.getState();
      store.setSalesAngles(store.salesAngles.map((a: SalesAngle) =>
        a.id === currentAngles[0].name ? { ...a, description: newDescription } : a
      ));
    }
  };

  // Change angle for a funnel
  const handleChangeAngle = (funnelIndex: number, angleId: string) => {
    const angle = salesAngles.find(a => a.id === angleId);
    if (angle) {
      updateFunnelConfig(funnelIndex, {
        selectedAngles: [{ name: angle.id, description: angle.description }],
      });
    }
  };

  // Set custom angle for a funnel
  const handleSetCustomAngle = (funnelIndex: number, name: string, description: string) => {
    updateFunnelConfig(funnelIndex, {
      selectedAngles: [{ name, description }],
    });
  };

  // Navigation
  const handleNext = () => {
    if (activeSubStep === 1) {
      if (totalSelected === 0) return;

      // Initialize all funnels: new templates first, then legacy
      const total = newCount + legacyCount;
      initializeFunnels(total);

      // Apply designs only to the new-template funnels (first newCount)
      if (newCount > 0) {
        setTimeout(() => {
          selectedTemplateOrder.forEach((templateId, index) => {
            const template = sectionTemplates.find(t => t.id.toString() === templateId);
            if (template) {
              setFunnelDesign(index, templateId, template.type, designRecommendation?.recommended_sections);
            }
          });
        }, 0);
      }
      setActiveSubStep(2);
    } else if (activeSubStep === 2) {
      // Always show ads step — both legacy and new templates can have ads
      setAdsSidebarFunnel(0);
      setActiveSubStep(3);
    } else if (activeSubStep === 3) {
      onComplete?.();
    }
  };

  const handleBack = () => {
    if (activeSubStep > 1) {
      setActiveSubStep((activeSubStep - 1) as 1 | 2 | 3);
    } else {
      onBack?.();
    }
  };

  const getNextLabel = () => {
    if (activeSubStep === 3) {
      return dictionary["continue"]?.[language] ?? "Continuar";
    }
    return dictionary["next"]?.[language] ?? dictionary["continue"]?.[language] ?? "Siguiente";
  };

  const isNextDisabled =
    (activeSubStep === 1 && totalSelected === 0) ||
    (activeSubStep === 2 && (isFetchingSalesAngleRecommendation || salesAngles.length === 0));

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* No stepper header — hidden per user request */}

      {/* Sub-step content */}
      <div className="flex-1 min-h-0">
        <div className={activeSubStep !== 1 ? 'hidden' : 'h-full'}>
          <DesignTabGlobal
            sectionTemplates={sectionTemplates}
            isFetchingTemplates={isFetchingTemplates}
            selectedTemplateOrder={selectedTemplateOrder}
            onToggleTemplate={handleToggleTemplate}
            selectedLegacyTemplates={selectedLegacyTemplates}
            onToggleLegacyTemplate={handleToggleLegacyTemplate}
            totalSelected={totalSelected}
            language={language}
          />
        </div>

        <div className={activeSubStep !== 2 ? 'hidden' : 'h-full min-h-0 flex flex-col'}>
          <AngleAssignmentTab
            funnels={funnels}
            sectionTemplates={sectionTemplates}
            selectedTemplateOrder={selectedTemplateOrder}
            selectedLegacyTemplates={selectedLegacyTemplates}
            newCount={newCount}
            salesAngles={salesAngles}
            isFetchingSalesAngleRecommendation={isFetchingSalesAngleRecommendation}
            onChangeAngle={handleChangeAngle}
            onUpdateAngleDescription={handleUpdateAngleDescription}
            onSetCustomAngle={handleSetCustomAngle}
            language={language}
          />
        </div>

        <div className={activeSubStep !== 3 ? 'hidden' : 'h-full'}>
          <AdsGlobalTab
            funnels={funnels}
            adTemplates={adTemplates}
            isFetchingAdTemplates={isFetchingAdTemplates}
            onToggleAdTemplate={(templateId) => {
              // Toggle ad for ALL funnels at once
              funnels.forEach((_, index) => toggleFunnelAdTemplate(index, templateId));
            }}
            language={language}
          />
        </div>
      </div>

      {/* Navigation buttons — fixed at bottom, never scrolls */}
      <div className="shrink-0 flex justify-between items-center pt-3">
        <button
          onClick={handleBack}
          className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          {dictionary["back"]?.[language] ?? "Atrás"}
        </button>
        <div className="flex items-center gap-3">
          {activeSubStep === 3 && (
            <button
              onClick={() => onComplete?.()}
              className="text-sm text-gray-400 hover:text-gray-600 transition-colors"
            >
              {dictionary["skip"]?.[language] ?? "Omitir"}
            </button>
          )}
          <button
            onClick={handleNext}
            disabled={isNextDisabled}
            className={`flex items-center gap-2 px-7 py-2.5 text-sm text-white rounded-full font-medium transition-all ${
              isNextDisabled
                ? 'bg-[#E2E8F0] cursor-not-allowed'
                : 'bg-[#3E3EFF] hover:bg-[#2d3a7f]'
            }`}
          >
            {getNextLabel()}
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Sub-step 1: Global Template Selection ──────────────────
function DesignTabGlobal({
  sectionTemplates,
  isFetchingTemplates,
  selectedTemplateOrder,
  onToggleTemplate,
  selectedLegacyTemplates,
  onToggleLegacyTemplate,
  totalSelected,
  language,
}: {
  sectionTemplates: SectionTemplate[];
  isFetchingTemplates: boolean;
  selectedTemplateOrder: string[];
  onToggleTemplate: (templateId: string, templateType: string) => void;
  selectedLegacyTemplates: LegacyTemplate[];
  onToggleLegacyTemplate: (template: LegacyTemplate) => void;
  totalSelected: number;
  language: Language;
}) {
  const hasTemplates = sectionTemplates.length > 0;
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  // Compute a global selection number across both types
  // New templates get numbers 1..N, legacy templates get N+1..N+M
  const newCount = selectedTemplateOrder.length;
  const getNewSelectionNumber = (templateId: string) => {
    const idx = selectedTemplateOrder.indexOf(templateId);
    return idx >= 0 ? idx + 1 : 0;
  };
  const getLegacySelectionNumber = (legacyId: string) => {
    const idx = selectedLegacyTemplates.findIndex(t => t.id === legacyId);
    return idx >= 0 ? newCount + idx + 1 : 0;
  };

  return (
    <div>
      <div className="mb-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-900">
            {dictionary["select-template-per-funnel"]?.[language] ?? "Select one template for each funnel you want to create"}
          </h2>
          {totalSelected > 0 && (
            <span className="text-sm font-bold text-[#3E3EFF] whitespace-nowrap ml-3">
              {totalSelected} {totalSelected === 1 ? 'funnel' : 'funnels'}
            </span>
          )}
        </div>
      </div>

      {isFetchingTemplates ? (
        <div className="flex items-center justify-center py-16">
          <Loader size={36} />
        </div>
      ) : (
        <div className="max-h-[calc(100vh-280px)] overflow-y-auto pr-1 py-1 space-y-6">
          {/* Classic/legacy templates — shown first */}
          <div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
              {dictionary["classic-templates-label"]?.[language] ?? "Plantillas clásicas"}
            </p>
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
              {LEGACY_TEMPLATES.map((template) => {
                const isSelected = selectedLegacyTemplates.some(t => t.id === template.id);
                const selectionNumber = getLegacySelectionNumber(template.id);
                const isHovered = hoveredId === template.id && !isSelected;

                return (
                  <button
                    key={template.id}
                    onClick={() => onToggleLegacyTemplate(template)}
                    onMouseEnter={() => setHoveredId(template.id)}
                    onMouseLeave={() => setHoveredId(null)}
                    className={`relative w-full rounded-lg overflow-hidden transition-all duration-300 ${
                      isSelected
                        ? 'ring-2 ring-[#3E3EFF]'
                        : 'hover:ring-1 hover:ring-gray-300'
                    } border border-gray-300`}
                  >
                    <div className="w-full aspect-[9/16] bg-gray-100 overflow-hidden">
                      <img
                        src={template.previewUrl}
                        alt={template.name}
                        className="w-full h-full object-cover"
                        style={{
                          objectPosition: isSelected
                            ? 'center top'
                            : isHovered
                              ? 'center bottom'
                              : 'center top',
                          transition: isSelected
                            ? 'none'
                            : isHovered
                              ? 'object-position 30s linear'
                              : 'object-position 0.5s ease-out',
                        }}
                        loading="lazy"
                      />
                    </div>
                    {/* Selection number badge */}
                    {isSelected && (
                      <div className="absolute top-2 right-2 w-6 h-6 bg-[#3E3EFF] rounded-full flex items-center justify-center shadow">
                        <span className="text-xs font-bold text-white">{selectionNumber}</span>
                      </div>
                    )}
                    {/* Style badge */}
                    <div className="absolute bottom-2 left-2 px-2 py-0.5 bg-black/50 rounded-full">
                      <span className="text-[10px] font-medium text-white">{template.style}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* New AI-generated templates — shown after classic */}
          {hasTemplates && (
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                {dictionary["ai-templates-label"]?.[language] ?? "Plantillas nuevas"}
              </p>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
                {sectionTemplates.map((template) => {
                  const templateId = template.id.toString();
                  const isSelected = selectedTemplateOrder.includes(templateId);
                  const selectionNumber = getNewSelectionNumber(templateId);
                  const isHovered = hoveredId === templateId && !isSelected;

                  return (
                    <button
                      key={template.id}
                      onClick={() => onToggleTemplate(templateId, template.type)}
                      onMouseEnter={() => setHoveredId(templateId)}
                      onMouseLeave={() => setHoveredId(null)}
                      className={`relative w-full rounded-lg overflow-hidden transition-all duration-300 ${
                        isSelected
                          ? 'opacity-50 ring-2 ring-[#3E3EFF]'
                          : 'hover:ring-1 hover:ring-gray-300'
                      } border border-gray-300`}
                    >
                      {template.url ? (
                        <div className="w-full aspect-[9/16] bg-gray-100 overflow-hidden">
                          <img
                            src={template.url}
                            alt={template.name}
                            className="w-full h-full object-cover"
                            style={{
                              objectPosition: isSelected
                                ? 'center top'
                                : isHovered
                                  ? 'center bottom'
                                  : 'center top',
                              transition: isSelected
                                ? 'none'
                                : isHovered
                                  ? 'object-position 30s linear'
                                  : 'object-position 0.5s ease-out',
                            }}
                            loading="lazy"
                          />
                        </div>
                      ) : (
                        <div className="w-full aspect-[9/16] bg-gradient-to-b from-[#6366F1] to-[#8B5CF6]" />
                      )}
                      {isSelected && (
                        <div className="absolute top-2 right-2 w-6 h-6 bg-[#3E3EFF] rounded-full flex items-center justify-center shadow">
                          <span className="text-xs font-bold text-white">{selectionNumber}</span>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Sub-step 2: Angle Assignment (template ↔ angle) ────────
function AngleAssignmentTab({
  funnels,
  sectionTemplates,
  selectedTemplateOrder,
  selectedLegacyTemplates,
  newCount,
  salesAngles,
  isFetchingSalesAngleRecommendation,
  onChangeAngle,
  onUpdateAngleDescription,
  onSetCustomAngle,
  language,
}: {
  funnels: FunnelConfig[];
  sectionTemplates: SectionTemplate[];
  selectedTemplateOrder: string[];
  selectedLegacyTemplates: LegacyTemplate[];
  newCount: number;
  salesAngles: SalesAngle[];
  isFetchingSalesAngleRecommendation: boolean;
  onChangeAngle: (funnelIndex: number, angleId: string) => void;
  onUpdateAngleDescription: (funnelIndex: number, newDescription: string) => void;
  onSetCustomAngle: (funnelIndex: number, name: string, description: string) => void;
  language: Language;
}) {
  const iconMap = [Clock, Users, Shield, Zap, Leaf];
  const [expandedFunnel, setExpandedFunnel] = useState<number | null>(null);
  const [editingFunnelIndex, setEditingFunnelIndex] = useState<number | null>(null);
  const [editedName, setEditedName] = useState('');
  const [editedDescription, setEditedDescription] = useState('');
  const [customFormFunnel, setCustomFormFunnel] = useState<number | null>(null);
  const [customName, setCustomName] = useState('');
  const [customDesc, setCustomDesc] = useState('');

  if (salesAngles.length === 0 || isFetchingSalesAngleRecommendation) {
    return <AnglesLoadingScreen language={language} />;
  }

  return (
    <div className="max-w-2xl mx-auto h-full flex flex-col min-h-0">
      <div className="min-h-0 flex-1 overflow-y-auto pr-1">
        <h2 className="text-lg font-bold text-gray-900 mb-4 text-center sticky-none">
          {dictionary["assign-angles-title"]?.[language] ?? "Ángulo de venta por embudo"}
        </h2>
        <div className="space-y-3 w-full">
        {funnels.map((funnel, funnelIndex) => {
          // First newCount funnels are new templates, rest are legacy
          const isLegacyFunnel = funnelIndex >= newCount;
          const legacyIndex = funnelIndex - newCount;
          const legacyTemplate = isLegacyFunnel ? selectedLegacyTemplates[legacyIndex] : null;
          const templateId = !isLegacyFunnel ? selectedTemplateOrder[funnelIndex] : null;
          const template = templateId ? sectionTemplates.find(t => t.id.toString() === templateId) : null;
          const legacyPreviewUrl = legacyTemplate?.previewUrl;
          const currentAngle = funnel.selectedAngles[0];
          const currentAngleObj = currentAngle
            ? salesAngles.find(a => a.id === currentAngle.name)
            : null;
          const isEditing = editingFunnelIndex === funnelIndex;
          const isExpanded = expandedFunnel === funnelIndex;
          const isCustomForm = customFormFunnel === funnelIndex;
          const Icon = currentAngleObj?.icon || iconMap[funnelIndex % iconMap.length];

          return (
            <div
              key={funnel.id}
              className="flex gap-5 p-4 rounded-2xl border border-[#E2E8F0] bg-[#FAFAFF]"
            >
              {/* Template preview — wider column, 9:16 aspect */}
              <div className="flex-shrink-0 w-32">
                {legacyPreviewUrl ? (
                  <div className="w-full aspect-[9/16] rounded-xl overflow-hidden border border-[#E2E8F0] shadow-sm">
                    <img src={legacyPreviewUrl} alt={legacyTemplate?.name} className="w-full h-full object-cover object-top" />
                  </div>
                ) : template?.url ? (
                  <div className="w-full aspect-[9/16] rounded-xl overflow-hidden border border-[#E2E8F0] shadow-sm">
                    <img src={template.url} alt={template.name} className="w-full h-full object-cover object-top" />
                  </div>
                ) : (
                  <div className="w-full aspect-[9/16] rounded-xl bg-gradient-to-b from-[#6366F1] to-[#8B5CF6]" />
                )}
              </div>

              {/* Angle info */}
              <div className="flex-1 min-w-0">
                {/* Funnel label */}
                <p className="text-sm text-gray-400 font-bold uppercase tracking-wider py-3">
                  {dictionary["funnel-label"]?.[language] ?? "Embudo"} {funnelIndex + 1}
                </p>

                {/* Current angle — display OR edit */}
                {isEditing ? (
                  <div className="space-y-3 mb-3">
                    <input
                      type="text"
                      value={editedName}
                      onChange={(e) => setEditedName(e.target.value)}
                      placeholder={dictionary["custom-angle-name-placeholder"]?.[language] ?? "Nombre del ángulo"}
                      className="w-full text-sm text-gray-700 border border-gray-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-1 focus:ring-[#3E3EFF]/50"
                      autoFocus
                    />
                    <textarea
                      value={editedDescription}
                      onChange={(e) => setEditedDescription(e.target.value)}
                      placeholder={dictionary["custom-angle-desc-placeholder"]?.[language] ?? "Describe tu enfoque de venta..."}
                      className="w-full text-sm text-gray-700 border border-gray-200 rounded-lg p-3 resize-none focus:outline-none focus:ring-1 focus:ring-[#3E3EFF]/50"
                      rows={3}
                    />
                    <div className="flex gap-2.5">
                      <button
                        onClick={() => {
                          onSetCustomAngle(funnelIndex, editedName.trim() || currentAngle?.name || '', editedDescription.trim());
                          setEditingFunnelIndex(null);
                        }}
                        className="px-5 py-2 text-sm font-medium text-white bg-[#3E3EFF] rounded-full hover:bg-[#3535e0] transition-colors"
                      >
                        {dictionary["save-changes"]?.[language] ?? "Guardar cambios"}
                      </button>
                      <button
                        onClick={() => setEditingFunnelIndex(null)}
                        className="px-5 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-full hover:bg-gray-50 transition-colors"
                      >
                        {dictionary.cancel?.[language] ?? "Cancelar"}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="mb-3">
                    <p className="text-base font-bold text-gray-900">
                      {currentAngleObj?.title || currentAngle?.name || '—'}
                    </p>
                    {currentAngle?.description && (
                      <p className="text-sm text-gray-500 leading-relaxed mt-1">
                        {currentAngle.description}
                      </p>
                    )}
                  </div>
                )}

                {/* Action buttons row */}
                {!isEditing && (
                  <div className="flex flex-wrap gap-2.5 justify-end mt-5">
                    <button
                      onClick={() => {
                        setEditingFunnelIndex(funnelIndex);
                        setEditedName(currentAngleObj?.title || currentAngle?.name || '');
                        setEditedDescription(currentAngle?.description || '');
                        setExpandedFunnel(null);
                      }}
                      className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-full hover:bg-gray-50 transition-colors shadow-[0_1px_2px_rgba(0,0,0,0.04)]"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                      {dictionary["edit-angle"]?.[language] ?? "Editar"}
                    </button>
                    <button
                      onClick={() => {
                        setExpandedFunnel(isExpanded ? null : funnelIndex);
                        setEditingFunnelIndex(null);
                      }}
                      className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-full hover:bg-gray-50 transition-colors shadow-[0_1px_2px_rgba(0,0,0,0.04)]"
                    >
                      {isExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                      {dictionary["other-angles"]?.[language] ?? "Otros ángulos"}
                    </button>
                  </div>
                )}

                {/* Expanded: other angle options */}
                {isExpanded && (
                  <div className="mt-3 space-y-2">
                    {salesAngles.map((angle) => {
                      const isActive = currentAngle?.name === angle.id;
                      return (
                        <button
                          key={angle.id}
                          onClick={() => {
                            onChangeAngle(funnelIndex, angle.id);
                            setExpandedFunnel(null);
                          }}
                          className={`w-full text-left px-4 py-3 rounded-xl transition-all ${
                            isActive
                              ? 'bg-white border-[1.5px] border-[#3E3EFF]/40 text-gray-900'
                              : 'bg-white border border-[#E2E8F0] text-gray-600 hover:border-gray-300'
                          }`}
                        >
                          <p className="text-sm font-semibold text-gray-900">{angle.title}</p>
                          {angle.description && (
                            <p className="text-sm text-gray-500 mt-1 leading-relaxed">{angle.description}</p>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          );
        })}
        </div>
      </div>
    </div>
  );
}

// ─── Sub-step 3: Ads with Funnel Sidebar ────────────────────
type AdStyleTab = 'all' | 'viral' | 'offer' | 'testimonials';
const AD_STYLE_TABS: AdStyleTab[] = ['all', 'viral', 'offer', 'testimonials'];
const MAX_AD_TEMPLATES = 20;

function AdsGlobalTab({
  funnels,
  adTemplates,
  isFetchingAdTemplates,
  onToggleAdTemplate,
  language,
}: {
  funnels: FunnelConfig[];
  adTemplates: AdStyle[];
  isFetchingAdTemplates: boolean;
  onToggleAdTemplate: (templateId: string) => void;
  language: Language;
}) {
  const [activeTab, setActiveTab] = useState<AdStyleTab>('all');

  // Use first funnel's selection as the global selection (all funnels are synced)
  const selectedTemplates = funnels[0]?.selectedAdTemplates || [];
  const isMaxReached = selectedTemplates.length >= MAX_AD_TEMPLATES;

  const filteredTemplates = useMemo(() => {
    if (activeTab === 'all') return adTemplates;
    return adTemplates.filter(t => t.category === activeTab);
  }, [adTemplates, activeTab]);

  const templateColumns = useMemo(() => {
    const cols: [AdStyle[], AdStyle[], AdStyle[], AdStyle[], AdStyle[]] = [[], [], [], [], []];
    filteredTemplates.forEach((t, i) => cols[i % 5].push(t));
    return cols;
  }, [filteredTemplates]);

  const getTabLabel = (tab: AdStyleTab): string => {
    const labels: Record<AdStyleTab, string> = {
      all: dictionary["all"]?.[language] ?? 'Todos',
      viral: dictionary["viral"]?.[language] ?? 'Viral',
      offer: dictionary["offer"]?.[language] ?? 'Oferta',
      testimonials: dictionary["testimonials"]?.[language] ?? 'Testimonios',
    };
    return labels[tab];
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-base font-bold text-gray-900">
          {dictionary["choose-ad-templates"]?.[language] ?? "Anuncios: Cada plantilla que elijas la convertiremos en un anuncio para tu producto"}
        </h2>
        {selectedTemplates.length > 0 && (
          <span className="text-sm font-bold text-[#3E3EFF] whitespace-nowrap ml-3">
            {selectedTemplates.length}
          </span>
        )}
      </div>

      {/* Category tabs */}
      <div className="flex gap-2 mb-3 overflow-x-auto scrollbar-hide whitespace-nowrap pb-1">
        {AD_STYLE_TABS.map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => setActiveTab(tab)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
              activeTab === tab
                ? 'bg-[#1E1E2E] text-white'
                : 'bg-white border border-[#E2E8F0] text-gray-500 hover:border-gray-300 hover:text-gray-700'
            }`}
          >
            {getTabLabel(tab)}
          </button>
        ))}
      </div>

      {isMaxReached && (
        <div className="mb-3 flex items-center gap-2 rounded-lg bg-amber-50 px-3 py-2">
          <div className="flex items-center justify-center w-5 h-5 rounded-full bg-amber-500 text-white text-[10px] font-bold flex-shrink-0">!</div>
          <p className="text-xs text-amber-700 font-medium">
            {(dictionary["max-ad-templates-reached"]?.[language] ?? "Máximo de {n} plantillas alcanzado").replace("{n}", String(MAX_AD_TEMPLATES))}
          </p>
        </div>
      )}

      {/* Templates grid — 5 columns */}
      {isFetchingAdTemplates ? (
        <div className="max-h-[calc(100vh-320px)] overflow-y-auto pr-1">
          <div className="flex gap-3">
            {[0, 1, 2, 3, 4].map((col) => (
              <div key={col} className="flex-1 flex flex-col gap-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div
                    key={i}
                    className="rounded-xl bg-gray-100 animate-pulse"
                    style={{ height: (col + i) % 3 === 0 ? '180px' : (col + i) % 3 === 1 ? '140px' : '160px' }}
                  />
                ))}
              </div>
            ))}
          </div>
        </div>
      ) : filteredTemplates.length > 0 ? (
        <div className="max-h-[calc(100vh-320px)] overflow-y-auto pr-1 p-1 -m-1">
          <div className="flex gap-3 items-start">
            {templateColumns.map((col, colIdx) => (
              <div key={colIdx} className="flex-1 flex flex-col gap-3">
                {col.map((template) => {
                  const isSelected = selectedTemplates.includes(template.id);
                  const disabled = isMaxReached && !isSelected;

                  return (
                    <div
                      key={template.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => !disabled && onToggleAdTemplate(template.id)}
                      onKeyDown={(e) => e.key === 'Enter' && !disabled && onToggleAdTemplate(template.id)}
                      className={`relative group cursor-pointer rounded-xl overflow-hidden transition-all duration-200 ${
                        isSelected
                          ? 'ring-2 ring-[#3E3EFF] ring-offset-2'
                          : disabled
                            ? 'opacity-40 cursor-not-allowed'
                            : 'hover:scale-[1.02]'
                      }`}
                    >
                      <LazyImage
                        src={template.coverImage}
                        alt={template.name}
                        className="w-full block"
                        wrapperClassName="w-full"
                      />
                      {isSelected && (
                        <div className="absolute top-1.5 right-1.5 w-5 h-5 bg-[#3E3EFF] rounded-full flex items-center justify-center z-10 shadow">
                          <Check className="h-3 w-3 text-white" strokeWidth={3} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-center h-32 text-gray-400">
          <p className="text-sm">No se encontraron plantillas</p>
        </div>
      )}
    </div>
  );
}

// ─── Angles Loading Screen (multi-step animation) ───────────
function AnglesLoadingScreen({ language }: { language: Language }) {
  const [activeStep, setActiveStep] = useState(0);

  const steps = [
    {
      icon: Sparkles,
      label: dictionary["analyzing-product"]?.[language] ?? "Haciendo investigación de mercado...",
    },
    {
      icon: Target,
      label: dictionary["loading-personalized-sales-angles"]?.[language] ?? "Buscando reseñas de productos similares...",
    },
    {
      icon: BarChart3,
      label: dictionary["optimizing-sections"]?.[language] ?? "Encontrando dolores de usuarios reales...",
    },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % steps.length);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="flex flex-col items-center justify-center py-12 px-4">
      <div className="relative mb-8">
        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#6366F1] to-[#3E3EFF] flex items-center justify-center animate-pulse">
          <Sparkles className="w-10 h-10 text-white" />
        </div>
        <div className="absolute inset-0 w-20 h-20 rounded-full bg-gradient-to-br from-[#6366F1] to-[#3E3EFF] opacity-20 animate-ping" />
      </div>

      <h2 className="text-xl md:text-2xl font-bold text-gray-900 text-center mb-2">
        {dictionary["fluxi-analyzing-title"]?.[language] ?? "Fluxi está analizando tu producto"}
      </h2>
      <p className="text-sm text-[#64748B] text-center mb-10 max-w-md">
        {dictionary["fluxi-analyzing-subtitle"]?.[language] ?? "Estamos encontrando los mejores ángulos de venta para tu producto"}
      </p>

      <div className="w-full max-w-sm space-y-3">
        {steps.map((step, index) => {
          const Icon = step.icon;
          const isActive = index === activeStep;
          const isDone = index < activeStep;

          return (
            <div
              key={index}
              className={`flex items-center gap-3 px-4 py-3 rounded-2xl transition-all duration-500 ${
                isActive
                  ? 'bg-[#F5F8FF] border border-[#3E3EFF]/20 shadow-sm'
                  : isDone
                    ? 'bg-[#F0FDF4] border border-green-200'
                    : 'bg-[#F8FAFC] border border-transparent'
              }`}
            >
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-500 ${
                  isActive
                    ? 'bg-[#3E3EFF] text-white'
                    : isDone
                      ? 'bg-green-500 text-white'
                      : 'bg-[#E2E8F0] text-gray-400'
                }`}
              >
                {isDone ? (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <Icon className="w-4 h-4" />
                )}
              </div>
              <span
                className={`text-sm font-medium transition-all duration-500 ${
                  isActive ? 'text-[#3E3EFF]' : isDone ? 'text-green-600' : 'text-gray-400'
                }`}
              >
                {step.label}
              </span>
              {isActive && (
                <div className="ml-auto">
                  <Loader size={16} color="#3E3EFF" />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
